import { modelTitleState } from '@/components/canvas/utils/modelTitle'

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { type NodeProps, useReactFlow, useNodeConnections, useNodesData, useStore } from '@xyflow/react'
import { useParams } from '@tanstack/react-router'
import { VideoNode } from '@/components/canvas/nodes'
import type { GenieNodeState, AiModelPurpose } from '@genieartinc/schemas'
import type { NodeOrchestratorState } from '@genieartinc/schemas'
import { useJobGeneration } from '@/hooks/useJobGeneration'
import { useJobStore } from '@/stores/jobStore'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { getInputNode } from '@/lib/canvas/utils/getInputNodeData'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import type { HandleMeta } from '@/lib/canvas/types'
import { PURPOSE_HANDLES } from '@/lib/canvas/configs/handles'
import { toast } from '@/components/ui/toast'
import { useNodeModels, getDefaultModel, findModelById } from '@/hooks/useNodeModels'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { usePinnedParameters } from '@/components/canvas/nodes/pinned-parameters/usePinnedParameters'
import { useJobEstimate } from '@/hooks/useJobEstimate'
import { isApiError } from '@/lib/api/api-helpers'
import { useInsufficientBalanceStore } from '@/components/canvas/nodes/base/insufficient-balance-store'

/**
 * VideoNodeNode - React Flow custom node
 *
 * Wraps the existing VideoNode variant component with React Flow handles
 * and wires up video generation via the AI generation pipeline.
 */

export interface VideoNodeNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

/** Purpose-based handles from centralized config */
const HANDLES_BY_PURPOSE = PURPOSE_HANDLES.VideoNode

const DEFAULT_VIDEO_HANDLES = HANDLES_BY_PURPOSE.create

function VideoNodeNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as VideoNodeNodeData
  const { state: genieState, onHoverChange } = nodeData
  const purpose = (genieState.purpose as string) || undefined
  const onStateChange = useNodeStateChange(id)
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = canvasIdParam as string | undefined

  const reactFlowInstance = useReactFlow()
  const { submitJob } = useJobGeneration()
  const subPurpose = (genieState.subPurpose as string) || undefined
  // Tool nodes (lipsync, reframe…) must only see their own tool's models, like ImageNodeNode
  const { data: videoModels } = useNodeModels('video', undefined, (purpose as AiModelPurpose) || 'create', subPurpose)
  const { balance: walletBalance } = useWalletBalance()

  const currentPromptRef = useRef(genieState.prompt || '')
  const isGeneratingRef = useRef(false)
  const genieStateRef = useRef(genieState)
  genieStateRef.current = genieState
  // Ref for error-path-only value — avoids handleGenerate recreation on wallet polling
  const walletBalanceRef = useRef(walletBalance)
  walletBalanceRef.current = walletBalance

  const loading = false
  const error = null

  const [orchestratorState, setOrchestratorState] = useState<NodeOrchestratorState>({
    status: 'idle',
  })

  useEffect(() => {
    if (orchestratorState.status === 'success') {
      const timer = setTimeout(() => setOrchestratorState({ status: 'idle' }), 3000)
      return () => clearTimeout(timer)
    }
  }, [orchestratorState.status])

  // When WebSocket completion unlocks the node, reset orchestrator state
  useEffect(() => {
    if (genieState.locked === false && orchestratorState.status === 'working') {
      setOrchestratorState({ status: 'idle' })
      isGeneratingRef.current = false
    }
  }, [genieState.locked, orchestratorState.status])

  // Reactively resolve connected image URL from upstream node (for reference frame)
  const connectedImage = useStore(useCallback((s) => {
    const targetNode = s.nodes.find((n) => n.id === id)
    const handles = (targetNode?.data as Record<string, unknown> | undefined)?.handles as Array<{ id: string; type: string; dataType?: string }> | undefined
    const imageHandleIds = new Set<string>()

    if (handles) {
      for (const h of handles) {
        if (h.type === 'input' && h.dataType === 'image') {
          imageHandleIds.add(h.id)
        }
      }
    }
    imageHandleIds.add('image-input')

    const incomingEdges = s.edges.filter((e) => e.target === id && imageHandleIds.has(e.targetHandle || ''))
    if (incomingEdges.length === 0) return null

    for (const edge of incomingEdges) {
      const sourceNode = s.nodes.find((n) => n.id === edge.source)
      if (!sourceNode) continue

      const sourceData = sourceNode.data as Record<string, unknown> | undefined
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const sourceType = sourceNode.type || ''
      if (sourceType === 'ImageFile') {
        const fileMeta = sourceState.fileMetadata as Record<string, unknown> | undefined
        if (fileMeta?.publicUrl) return fileMeta.publicUrl as string
      }
      if (sourceType === 'ImageNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const lastImage = createdItems?.filter(i => i.type === 'image').pop()
        if (lastImage?.content) return lastImage.content
      }
      const images = sourceState.images as string[] | undefined
      if (images && images.length > 0) return images[images.length - 1]
    }
    return null
  }, [id]))

  // Reactively read connected prompt from upstream text node
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(
    () => incomingConnections.map(c => c.source),
    [incomingConnections],
  )
  const sourceNodesData = useNodesData(sourceNodeIds)

  const connectedPrompt = useMemo(() => {
    if (sourceNodesData.length === 0) return null

    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      const sourceData = nodeEntry.data as Record<string, unknown>
      const sourceState = sourceData?.state as Record<string, unknown> | undefined
      if (!sourceState) continue

      const sourceType = nodeEntry.type || ''
      if (sourceType === 'TextFile' || sourceType === 'TextNode') {
        const createdItems = sourceState.createdItems as Array<{ type: string; content: string }> | undefined
        const latestText = createdItems?.[createdItems.length - 1]
        if (latestText?.type === 'text' && typeof latestText.content === 'string' && latestText.content.trim()) {
          return latestText.content
        }
        const prompt = sourceState.prompt
        if (typeof prompt === 'string' && prompt.trim()) return prompt
      }
    }
    return null
  }, [sourceNodesData])

  const handleChange = useCallback((dataOrUpdater: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
    if (typeof dataOrUpdater === 'function') {
      onStateChange((prev) => {
        const result = dataOrUpdater(prev)
        if (result.prompt !== undefined) currentPromptRef.current = result.prompt as string
        if (result.orchestrator !== undefined) {
          setOrchestratorState(result.orchestrator as NodeOrchestratorState)
          const { orchestrator, ...rest } = result
          return rest
        }
        return result
      })
      return
    }

    const d = dataOrUpdater
    if (d.prompt !== undefined) {
      currentPromptRef.current = d.prompt as string
      onStateChange(prev => ({ ...prev, prompt: d.prompt }))
    }
    if (d.title !== undefined) {
      onStateChange(prev => ({ ...prev, title: d.title, titleSource: 'custom', data: { ...(prev.data as Record<string, unknown> || {}), title: d.title } }))
    }
    if (d.images !== undefined) onStateChange(prev => ({ ...prev, images: d.images }))
    if (d.createdItems !== undefined) onStateChange(prev => ({ ...prev, createdItems: d.createdItems }))
    if (d.collapsed !== undefined) onStateChange(prev => ({ ...prev, collapsed: d.collapsed }))
    if (d.orchestrator !== undefined) setOrchestratorState(d.orchestrator as NodeOrchestratorState)
  }, [onStateChange])

  const handleSelect = useCallback(() => {}, [])

  // Resolve active model for cost estimation
  const defaultModel = useMemo(() => getDefaultModel(videoModels), [videoModels])
  const activeModel = useMemo(() => {
    const savedModelId = typeof genieState.modelId === 'string' ? genieState.modelId : undefined
    const savedModelValid = savedModelId && videoModels.some(m => m.id === savedModelId)
    const resolvedId = (savedModelValid ? savedModelId : undefined) || defaultModel?.id
    if (!resolvedId) return defaultModel
    return findModelById(videoModels, resolvedId) || defaultModel
  }, [videoModels, genieState.modelId, defaultModel])

  const { estimate: costEstimate } = useJobEstimate(activeModel?.provider ?? null, activeModel?.id ?? null, genieState.settings as Record<string, unknown> | undefined)
  // Ref for error-path-only value — avoids handleGenerate recreation on cost recalc
  const costEstimateRef = useRef(costEstimate)
  costEstimateRef.current = costEstimate
  const showInsufficientBalance = useInsufficientBalanceStore((s) => s.showInsufficientBalance)

  const handleGenerate = useCallback(async () => {
    // Prevent concurrent double-click generation
    if (isGeneratingRef.current) return
    isGeneratingRef.current = true

    const bridge = useCanvasBridgeStore.getState()
    const inputData = getInputNode(id, reactFlowInstance.getNodes(), reactFlowInstance.getEdges())

    const inputPrompt = Array.isArray(inputData.prompt) ? inputData.prompt[0] : inputData.prompt
    const finalPrompt = inputPrompt || connectedPrompt || currentPromptRef.current

    // Resolve reference image from connected node
    const finalImage = (Array.isArray(inputData.image) ? inputData.image[0] : inputData.image) || connectedImage || undefined

    const promptString = typeof finalPrompt === 'string' ? finalPrompt : ''

    if (!promptString?.trim()) {
      setOrchestratorState({ status: 'error', message: 'Please enter a prompt or connect a text node' })
      isGeneratingRef.current = false
      return
    }

    const panelSettings = bridge.settingsApi?.getSettings()
    const defaultModel = getDefaultModel(videoModels)
    const currentGenieState = genieStateRef.current
    const savedModelValid = currentGenieState.modelId && videoModels.some(m => m.id === currentGenieState.modelId)
    const resolvedModelId = (panelSettings?.modelId as string) || (savedModelValid ? currentGenieState.modelId : undefined) || defaultModel?.id
    if (!resolvedModelId) {
      toast.error('Please select a model before generating')
      setOrchestratorState({ status: 'error', message: 'No model selected' })
      isGeneratingRef.current = false
      return
    }

    const currentSettings = {
      modelId: resolvedModelId as string,
      modelFamilySlug: (panelSettings?.modelFamilySlug as string) || (currentGenieState.modelFamilySlug as string) || (currentGenieState.brandFamily as string) || defaultModel?.modelFamilySlug || '',
      settings: (panelSettings?.settings as Record<string, unknown>) || (currentGenieState.settings as Record<string, unknown>) || {},
    }

    // Pre-flight balance check
    if (costEstimate?.priceUsd && walletBalance) {
      const cost = parseFloat(costEstimate.priceUsd)
      const balance = parseFloat(walletBalance)
      if (!isNaN(cost) && !isNaN(balance) && balance < cost) {
        showInsufficientBalance(costEstimate.priceUsd, walletBalance)
        isGeneratingRef.current = false
        return
      }
    }

    // Submit job to queue (PRIMARY path — async processing via worker)
    try {
      const jobId = await submitJob({
        nodeId: id,
        nodeType: 'video',
        purpose,
        subPurpose,
        prompt: promptString,
        modelId: currentSettings.modelId,
        settings: {
          ...currentSettings.settings,
          image: finalImage,
        },
        variantCount: 1,
      })

      if (!jobId) {
        setOrchestratorState({ status: 'error', message: 'Failed to submit generation job' })
        isGeneratingRef.current = false
        return
      }

      // Create placeholder VideoFile node on canvas
      const placeholderIds: string[] = bridge.createPlaceholderCallback?.({
        parentNodeId: id,
        type: 'video',
        count: 1,
      }) ?? []

      // Store placeholder mapping so WebSocket handler can update them
      const jobStore = useJobStore.getState()
      jobStore.setPlaceholders(jobId, id, placeholderIds)

      // Lock parent node and set queued state
      onStateChange(prev => ({
        ...prev,
        locked: true,
        prompt: promptString,
        modelId: currentSettings.modelId,
        settings: currentSettings.settings,
      }))
      setOrchestratorState({ status: 'working', message: 'Queued for generation...', progress: 0, startedAt: new Date() })
    } catch (err) {
      // Always unlock the node and reset guard on any error
      onStateChange(prev => ({ ...prev, locked: false }))
      isGeneratingRef.current = false

      if (isApiError(err, 'INSUFFICIENT_BALANCE')) {
        setOrchestratorState({ status: 'idle' })
        showInsufficientBalance(costEstimateRef.current?.priceUsd || '—', walletBalanceRef.current || '0')
      } else {
        console.error('Job submission failed:', err)
        setOrchestratorState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to start generation' })
      }
    }
  }, [submitJob, id, onStateChange, connectedPrompt, connectedImage, videoModels, purpose, subPurpose, showInsufficientBalance])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts
  const handleGenerateRef = useRef(handleGenerate)
  handleGenerateRef.current = handleGenerate

  useEffect(() => {
    if (!action) return
    switch (action.type) {
      case 'generate': handleGenerateRef.current(); break
      case 'cancel': setOrchestratorState({ status: 'idle' }); break
      case 'clear': onStateChange(prev => ({ ...prev, createdItems: [] })); break
      case 'update':
        if (action.params?.prompt !== undefined) {
          currentPromptRef.current = action.params.prompt
          onStateChange(prev => ({ ...prev, prompt: action.params.prompt }))
        }
        break
    }
    onStateChange(prev => ({ ...prev, _action: null }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionTimestamp, action, onStateChange])

  // Pinned parameters support
  const pinnedNames = useMemo(() => {
    const raw = genieState.pinnedParameters
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [genieState.pinnedParameters])

  const { parameters: pinnedParams, values: pinnedValues } = usePinnedParameters({
    pinnedNames,
    nodeType: 'video',
    modelFamilySlug: (genieState.modelFamilySlug as string | undefined) || (genieState.brandFamily as string | undefined) || activeModel?.modelFamilySlug,
    modelId: (genieState.modelId as string | undefined) || activeModel?.id,
    purpose: (purpose as AiModelPurpose) || 'create',
    settings: genieState.settings as Record<string, unknown> | undefined,
  })

  const handlePinnedParameterChange = useCallback((paramName: string, value: unknown) => {
    onStateChange(prev => ({
      ...prev,
      settings: {
        ...((prev.settings as Record<string, unknown>) || {}),
        [paramName]: value,
      },
    }))
  }, [onStateChange])

  const pinnedModelsList = useMemo(() => {
    if (!pinnedNames.includes('__model__')) return undefined
    return videoModels.map(m => ({ id: m.id, name: m.name }))
  }, [videoModels, pinnedNames])

  const handlePinnedModelChange = useCallback((modelId: string) => {
    const model = findModelById(videoModels, modelId)
    if (!model) return
    onStateChange(prev => ({
      ...prev,
      ...modelTitleState(prev, model.name),
      modelId: model.id,
      modelFamilySlug: model.modelFamilySlug,
    }))
  }, [videoModels, onStateChange])

  const nodeProps = {
    id,
    selected: selected || genieState.selected,
    initialData: {
      ...genieState,
      ...(activeModel?.name ? modelTitleState(genieState, activeModel.name) : {}),
    },
    connectedPrompt,
    connectedImage,
    orchestratorState,
    onChange: handleChange,
    onStateChange,
    onSelect: handleSelect,
    onGenerate: handleGenerate,
    onHoverChange,
    loading,
    error,
    canvasId: (genieState.canvasId as string) || canvasId,
    costProvider: activeModel?.provider ?? null,
    costModel: activeModel?.id ?? null,
    walletBalance: walletBalance ?? undefined,
    pinnedParameters: pinnedParams.length > 0 ? pinnedParams : undefined,
    pinnedParameterValues: pinnedParams.length > 0 ? pinnedValues : undefined,
    onPinnedParameterChange: (pinnedParams.length > 0 || pinnedNames.includes('__model__')) ? handlePinnedParameterChange : undefined,
    pinnedModelName: pinnedNames.includes('__model__') ? (activeModel?.name || activeModel?.id || 'No model') : undefined,
    pinnedModels: pinnedModelsList,
    onPinnedModelChange: pinnedNames.includes('__model__') ? handlePinnedModelChange : undefined,
  }

  // Purpose-specific handles — component owns these (not factory)
  const purposeHandles = (purpose && HANDLES_BY_PURPOSE[purpose]) || DEFAULT_VIDEO_HANDLES
  const handles: HandleMeta[] = nodeData.handles || purposeHandles

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <VideoNode {...nodeProps} purpose={purpose} />
    </>
  )
}

export const VideoNodeNode = React.memo(
  VideoNodeNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as VideoNodeNodeData
    const nextData = nextProps.data as unknown as VideoNodeNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.titleSource === nextData.state.titleSource &&
      prevData.state.modelName === nextData.state.modelName &&
      prevData.state.prompt === nextData.state.prompt &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.locked === nextData.state.locked &&
      prevData.state.images === nextData.state.images &&
      prevData.state.createdItems === nextData.state.createdItems &&
      prevData.state._action === nextData.state._action &&
      prevData.state.pinnedParameters === nextData.state.pinnedParameters &&
      prevData.state.settings === nextData.state.settings &&
      prevData.state.purpose === nextData.state.purpose &&
      prevData.state.modelId === nextData.state.modelId &&
      prevData.state.modelFamilySlug === nextData.state.modelFamilySlug &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)
VideoNodeNode.displayName = 'VideoNodeNode'
